export type PortionDishInput = {
  id: number;
  min?: number | null;
  price?: number | null;
  selected: boolean;
};

export type PortionDishResult = {
  id: number;
  quantity: number;
  selected: boolean;
};

export type PortionDistributionMode = 'set' | 'station';

export const perPersonUnits = (
  maxPerPerson: number | null | undefined,
  minPerPerson: number | null | undefined,
): number => {
  if (maxPerPerson != null && maxPerPerson > 0) {
    return maxPerPerson;
  }
  return minPerPerson != null && minPerPerson > 0 ? minPerPerson : 1;
};

export const hasPerPersonBudget = (
  maxPerPerson: number | null | undefined,
  minPerPerson: number | null | undefined,
): boolean =>
  (maxPerPerson != null && maxPerPerson > 0) || (minPerPerson != null && minPerPerson > 0);

export const distributeSubgroupPortions = (
  budget: number,
  dishesInOrder: PortionDishInput[],
  mode: PortionDistributionMode,
): PortionDishResult[] => {
  const total = Math.floor(budget);

  if (mode === 'set') {
    const selected = rankByPrice(dishesInOrder.filter(dish => dish.selected));
    const units =
      selected.length === 0 ? new Map<number, number>() : waterFill(Math.max(0, total), selected);
    return toResults(dishesInOrder, units);
  }

  if (dishesInOrder.length === 0 || total <= 0) {
    return toResults(dishesInOrder, new Map());
  }
  const ranked = rankByPrice(dishesInOrder);
  const chosen: PortionDishInput[] = [];
  let left = total;
  for (const dish of ranked) {
    const min = minPortions(dish);
    if (left < min) {
      break;
    }
    left -= min;
    chosen.push(dish);
  }
  if (chosen.length === 0) {
    chosen.push(ranked[0]);
  }
  return toResults(dishesInOrder, waterFill(total, chosen));
};

const waterFill = (budget: number, ranked: PortionDishInput[]): Map<number, number> => {
  const units = new Map<number, number>();
  let free = ranked;
  let remaining = budget;
  let fixedAny = true;
  while (fixedAny && free.length > 0) {
    const share = Math.floor(remaining / free.length);
    const stillFree: PortionDishInput[] = [];
    fixedAny = false;
    for (const dish of free) {
      const min = minPortions(dish);
      if (share < min) {
        units.set(dish.id, min);
        remaining -= min;
        fixedAny = true;
      } else {
        stillFree.push(dish);
      }
    }
    free = stillFree;
  }
  if (free.length > 0) {
    const share = Math.floor(remaining / free.length);
    const remainder = remaining - share * free.length;
    free.forEach((dish, index) => units.set(dish.id, share + (index === 0 ? remainder : 0)));
  }
  return units;
};

const toResults = (
  dishesInOrder: PortionDishInput[],
  units: Map<number, number>,
): PortionDishResult[] =>
  dishesInOrder.map(dish => {
    const quantity = units.get(dish.id);
    return quantity == null
      ? { id: dish.id, quantity: 0, selected: false }
      : { id: dish.id, quantity, selected: true };
  });

const rankByPrice = (dishesInOrder: PortionDishInput[]): PortionDishInput[] =>
  dishesInOrder.toSorted((left, right) => (right.price ?? 0) - (left.price ?? 0));

const minPortions = (dish: PortionDishInput): number =>
  dish.min == null ? 1 : Math.max(1, Math.floor(dish.min));
